import { LitElement, html, css } from 'lit';
import { styleMap } from 'lit-html/directives/style-map.js';
import importedCaretUp from '../assets/svg/caret-up.svg';
import importedCaretDown from '../assets/svg/caret-down.svg';
import importedCaretUpLighter from '../assets/svg/caret-up-lighter.svg';
import importedCaretDownLighter from '../assets/svg/caret-down-lighter.svg';
import '../cbom-product/CbomProduct';

const caretUp = `data:image/svg+xml;base64,${btoa(importedCaretUp)}`;
const caretDown = `data:image/svg+xml;base64,${btoa(importedCaretDown)}`;
const caretUpLighter = `data:image/svg+xml;base64,${btoa(
  importedCaretUpLighter
)}`;
const caretDownLighter = `data:image/svg+xml;base64,${btoa(
  importedCaretDownLighter
)}`;

class CbomCategory extends LitElement {
  // Properties
  static properties = {
    id: { type: Number },
    name: { type: String },
    open: { type: Boolean },
    isMobile: { type: Boolean },
    _heightCache: { state: true },
  };

  // Styles
  static styles = css`
    .category {
      overflow-y: hidden;
      display: block;
      transition: height 250ms ease-out;
    }
    .category-trigger {
      cursor: pointer;
    }
    .products {
      padding-left: 55px;
    }
    .is-mobile .products {
      padding-left: 20px;
    }
    h3 {
      margin: 7px;
      font-size: 15px;
      line-height: 20px;
      font-family: Arial, sans-serif;
    }
    .icon-caret {
      position: absolute;
      top: -17px;
      right: 15px;
      width: 18px;
      fill: #808080;
    }
    .is-mobile .icon-caret {
      width: 13px;
    }
    .category-name {
      margin: 0 7px;
      padding-top: 12px;
      color: #666666;
      min-height: 20px;
    }
    .position-relative {
      position: relative;
    }
  `;

  constructor() {
    super();

    // Storage of open and closed heights for transitioning
    this._heightCache = {
      open: 0,
      closed: 0,
    };
  }

  async firstUpdated() {
    // Give the browser a chance to paint
    await new Promise((r) => setTimeout(r, 0));

    // Store the open height to use when animating
    const clientRects = this.getClientRects();

    this._heightCache.open = clientRects[1].height;
    this._heightCache.closed =
      this.shadowRoot.querySelector('.category-trigger').offsetHeight;

    this.requestUpdate();
  }

  // Click handler to expand category, dispatches event to close other open categories
  async _onClick() {
    this.open = !this.open;

    // Dispatch event to close other open categories
    this.dispatchEvent(
      new CustomEvent('cbomcategoryclick', {
        detail: {
          id: this.id,
          open: this.open,
        },
      })
    );
  }

  _getHeightStyle() {
    if (this._heightCache.open && this._heightCache.closed) {
      return this.open
        ? `${this._heightCache.open}px`
        : `${this._heightCache.closed}px`;
    }
  }

  _getCaretSrc() {
    if (this.isMobile) {
      return this.open ? caretUpLighter : caretDownLighter;
    }
    return this.open ? caretUp : caretDown;
  }

  // Render
  render() {
    return html`
      <div
        class="
          category
          ${this.isMobile ? 'is-mobile' : ''}
        "
        style=${styleMap({ height: this._getHeightStyle() })}
      >
        <div class="category-trigger" @click=${this._onClick}>
          <h3 data-category_id="${this.id}" class="category-name">
            ${this.name}
          </h3>
          <div class="position-relative">
            <img class="icon-caret" src=${this._getCaretSrc()} alt="" />
            <slot name="current"></slot>
          </div>
        </div>
        <div class="products">
          <slot></slot>
        </div>
      </div>
    `;
  }
}

customElements.define('cbom-category', CbomCategory);
